import React, { MouseEventHandler, PropsWithChildren } from 'react'
import clsx from 'clsx'
import { Banner as BannerStyles } from '@airbus/elements/elements'
import { createUseStyles } from '../../lib/styling'
import useDeprecatedProps from '../../lib/hooks/useDeprecatedProps'
import useTimeout from '../../lib/hooks/useTimeout'
import { CommonProps } from '../helperTypes'

type Variants = 'info' | 'warning' | 'error' | 'success'

type Props = PropsWithChildren<{
  /** Delay in ms before onClose is automatically called. */
  autoHideDuration?: number
  /** Callback fired when the remove button is clicked. If set, the remove button will be shown. */
  onClose?: () => void
  /** The color variant */
  variant?: Variants
}>

type DeprecatedProps = {
  status?: Variants
  center?: boolean
  title?: string
  closable?: boolean
  visible?: boolean
}

export type BannerProps = Props & DeprecatedProps & CommonProps

const handleDeprecatedProps = (props: BannerProps): BannerProps => {
  const { status, center, title, closable, visible, ...rest } = props
  return {
    variant: status,
    ...rest,
  }
}

const useStyles = createUseStyles(BannerStyles, 'banner')

const RemoveButton = ({ onClick }: { onClick: MouseEventHandler<HTMLButtonElement> }) => {
  const classes = useStyles()
  return (
    <button
      type='button'
      className={classes.removeBtn}
      aria-label='Remove'
      onClick={onClick}
      onTouchEnd={(e: any) => {
        e.preventDefault()
        onClick && onClick(e)
      }}
      onMouseDown={(e) => {
        e.preventDefault()
        e.stopPropagation()
      }}
    />
  )
}

const Banner = (props: BannerProps) => {
  const { className, children, variant = 'info', onClose, autoHideDuration, ...rest } = handleDeprecatedProps(props)
  useDeprecatedProps(
    'Banner',
    {
      status: 'variant',
      center: undefined,
      title: undefined,
      closable: 'onClose',
      visible: undefined,
    },
    props,
  )
  const classes = useStyles()

  const handleClose = () => onClose && onClose()
  useTimeout(handleClose, autoHideDuration)

  const role = variant === 'info' || variant === 'success' ? 'status' : 'alert'
  return (
    <div className={clsx(className, classes.root, classes[`_${variant}`])} role={role} {...rest}>
      {children}
      {onClose && <RemoveButton onClick={onClose} />}
    </div>
  )
}

export default Banner
